import moment from 'moment';
import momentTimezone from 'moment-timezone';
import qs from 'qs';

const ARRAY_FILTER_KEYS = [
  ['organization_ids', 'organizationIds'],
  ['product_ids', 'productIds'],
  ['product_group_ids', 'productGroupIds'],
  ['order_types', 'orderTypes']
];

const VALID_MODES = ['all', 'include', 'exclude'];
const VALID_GRANULARITIES = ['auto', 'day', 'week', 'month', 'year'];
const VALID_COMPARISONS = [
  'none',
  'previous_period',
  'previous_quarter',
  'previous_calendar_year'
];
const VALID_ORDER_TYPES = ['billable', 'direct_allocation'];

export const DEFAULT_REPORT_TIMEZONE = 'Europe/Bucharest';
export const DEFAULT_REPORT_TAB = 'summary';
export const DEFAULT_PERIOD_LENS = 'marketing';
export const REPORT_TABS = ['summary', 'products', 'periods', 'organizations', 'activity'];
export const DEFAULT_PRODUCT_TABLE = {
  page: 1,
  perPage: 25,
  sortBy: 'net_sales_without_vat',
  sortDirection: 'desc'
};
export const DEFAULT_ORGANIZATION_TABLE = {
  page: 1,
  perPage: 25,
  sortBy: 'net_sales_without_vat',
  sortDirection: 'desc'
};

const VALID_PRODUCT_SORTS = [
  'name',
  'fulfilled_quantity',
  'net_sales_without_vat',
  'net_sales_with_vat',
  'orders'
];
const VALID_ORGANIZATION_SORTS = [...VALID_PRODUCT_SORTS];
const VALID_PERIOD_LENSES = ['marketing', 'financial'];
export const QUARTER_PRESETS = ['q1', 'q2', 'q3', 'q4'];
export const REPORT_PERIOD_PRESETS = [
  '7d',
  '30d',
  '3m',
  '6m',
  'ytd',
  ...QUARTER_PRESETS,
  'custom'
];

function positiveInteger(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function asArray(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }

  return value ? [String(value)] : [];
}

function validDate(value, fallback) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '') && moment(value, 'YYYY-MM-DD', true).isValid()
    ? value
    : fallback;
}

export function datesForPreset(preset, referenceDate = moment()) {
  const today = momentTimezone(referenceDate)
    .tz(DEFAULT_REPORT_TIMEZONE)
    .startOf('day');
  const dateTo = today.format('YYYY-MM-DD');
  let dateFrom;

  switch (preset) {
    case '7d':
      dateFrom = today.clone().subtract(6, 'days');
      break;
    case '3m':
      dateFrom = today.clone().subtract(3, 'months').add(1, 'day');
      break;
    case '6m':
      dateFrom = today.clone().subtract(6, 'months').add(1, 'day');
      break;
    case 'ytd':
      dateFrom = today.clone().startOf('year');
      return {
        dateFrom: dateFrom.format('YYYY-MM-DD'),
        dateTo: today.clone().endOf('year').format('YYYY-MM-DD')
      };
    case 'q1':
    case 'q2':
    case 'q3':
    case 'q4': {
      const quarter = Number(preset.slice(1));
      const quarterStart = today.clone().startOf('year').add(quarter - 1, 'quarters');
      return {
        dateFrom: quarterStart.format('YYYY-MM-DD'),
        dateTo: quarterStart.clone().endOf('quarter').format('YYYY-MM-DD')
      };
    }
    case '30d':
    default:
      dateFrom = today.clone().subtract(29, 'days');
      break;
  }

  return { dateFrom: dateFrom.format('YYYY-MM-DD'), dateTo };
}

export function comparisonForPreset(preset) {
  return QUARTER_PRESETS.includes(preset) ? 'previous_quarter' : 'previous_period';
}

export function createDefaultReportFilters(referenceDate = moment()) {
  return {
    preset: 'ytd',
    ...datesForPreset('ytd', referenceDate),
    timezone: DEFAULT_REPORT_TIMEZONE,
    granularity: 'auto',
    compare: 'previous_calendar_year',
    organizationMode: 'all',
    organizationIds: [],
    productMode: 'all',
    productIds: [],
    productGroupMode: 'all',
    productGroupIds: [],
    orderTypeMode: 'all',
    orderTypes: []
  };
}

// Dashboard and Detailed Reports share the complete current-calendar-year
// boundary represented by the existing `ytd` preset identifier.
export function createDashboardReportFilters(referenceDate = moment()) {
  return createDefaultReportFilters(referenceDate);
}

function filterMode(query, modeKey, arrayKey, fallback) {
  if (VALID_MODES.includes(query[modeKey])) {
    return query[modeKey];
  }

  // Preserve older deep links that predate explicit mode serialization.
  return Object.prototype.hasOwnProperty.call(query, arrayKey) ? 'include' : fallback;
}

export function parseReportQuery(search, referenceDate = moment()) {
  const defaults = createDefaultReportFilters(referenceDate);
  const query = qs.parse(search || '', { ignoreQueryPrefix: true });
  const filters = {
    ...defaults,
    preset: REPORT_PERIOD_PRESETS.includes(query.period)
      ? query.period
      : defaults.preset,
    dateFrom: validDate(query.date_from, defaults.dateFrom),
    dateTo: validDate(query.date_to, defaults.dateTo),
    timezone: query.timezone || defaults.timezone,
    granularity: VALID_GRANULARITIES.includes(query.granularity)
      ? query.granularity
      : defaults.granularity,
    compare: VALID_COMPARISONS.includes(query.compare) ? query.compare : defaults.compare,
    organizationMode: filterMode(
      query, 'organization_mode', 'organization_ids', defaults.organizationMode
    ),
    productMode: filterMode(query, 'product_mode', 'product_ids', defaults.productMode),
    productGroupMode: filterMode(
      query, 'product_group_mode', 'product_group_ids', defaults.productGroupMode
    ),
    orderTypeMode: filterMode(query, 'order_type_mode', 'order_types', defaults.orderTypeMode)
  };

  ARRAY_FILTER_KEYS.forEach(([queryKey, stateKey]) => {
    if (Object.prototype.hasOwnProperty.call(query, queryKey)) {
      filters[stateKey] = asArray(query[queryKey]);
    }
  });
  filters.orderTypes = filters.orderTypes.filter(type => VALID_ORDER_TYPES.includes(type));
  [
    ['organizationMode', 'organizationIds'],
    ['productMode', 'productIds'],
    ['productGroupMode', 'productGroupIds'],
    ['orderTypeMode', 'orderTypes']
  ].forEach(([modeKey, valuesKey]) => {
    if (filters[modeKey] === 'all') {
      filters[valuesKey] = [];
    }
  });

  return {
    activeTab: REPORT_TABS.includes(query.report_tab) ? query.report_tab : DEFAULT_REPORT_TAB,
    periodLens: VALID_PERIOD_LENSES.includes(query.period_lens)
      ? query.period_lens
      : DEFAULT_PERIOD_LENS,
    filters,
    productTable: {
      page: positiveInteger(query.product_page, DEFAULT_PRODUCT_TABLE.page),
      perPage: [10, 25, 50, 100].includes(positiveInteger(query.product_per_page, 0))
        ? positiveInteger(query.product_per_page, DEFAULT_PRODUCT_TABLE.perPage)
        : DEFAULT_PRODUCT_TABLE.perPage,
      sortBy: VALID_PRODUCT_SORTS.includes(query.product_sort_by)
        ? query.product_sort_by
        : DEFAULT_PRODUCT_TABLE.sortBy,
      sortDirection: ['asc', 'desc'].includes(query.product_sort_direction)
        ? query.product_sort_direction
        : DEFAULT_PRODUCT_TABLE.sortDirection
    },
    organizationTable: {
      page: positiveInteger(query.organization_page, DEFAULT_ORGANIZATION_TABLE.page),
      perPage: [10, 25, 50, 100].includes(positiveInteger(query.organization_per_page, 0))
        ? positiveInteger(query.organization_per_page, DEFAULT_ORGANIZATION_TABLE.perPage)
        : DEFAULT_ORGANIZATION_TABLE.perPage,
      sortBy: VALID_ORGANIZATION_SORTS.includes(query.organization_sort_by)
        ? query.organization_sort_by
        : DEFAULT_ORGANIZATION_TABLE.sortBy,
      sortDirection: ['asc', 'desc'].includes(query.organization_sort_direction)
        ? query.organization_sort_direction
        : DEFAULT_ORGANIZATION_TABLE.sortDirection
    }
  };
}

export function serializeReportQuery(
  filters,
  activeTab = DEFAULT_REPORT_TAB,
  productTable = DEFAULT_PRODUCT_TABLE,
  periodLens = DEFAULT_PERIOD_LENS,
  organizationTable = DEFAULT_ORGANIZATION_TABLE
) {
  return qs.stringify({
    report_tab: activeTab,
    period_lens: VALID_PERIOD_LENSES.includes(periodLens) ? periodLens : DEFAULT_PERIOD_LENS,
    period: filters.preset,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    timezone: filters.timezone,
    granularity: filters.granularity,
    compare: filters.compare,
    organization_mode: filters.organizationMode,
    organization_ids: filters.organizationMode === 'all' ? [] : filters.organizationIds,
    product_mode: filters.productMode,
    product_ids: filters.productMode === 'all' ? [] : filters.productIds,
    product_group_mode: filters.productGroupMode,
    product_group_ids: filters.productGroupMode === 'all' ? [] : filters.productGroupIds,
    order_type_mode: filters.orderTypeMode,
    order_types: filters.orderTypeMode === 'all' ? [] : filters.orderTypes,
    product_page: productTable.page,
    product_per_page: productTable.perPage,
    product_sort_by: productTable.sortBy,
    product_sort_direction: productTable.sortDirection,
    organization_page: organizationTable.page,
    organization_per_page: organizationTable.perPage,
    organization_sort_by: organizationTable.sortBy,
    organization_sort_direction: organizationTable.sortDirection
  }, {
    addQueryPrefix: true,
    arrayFormat: 'brackets'
  });
}

export function reportFiltersEqual(left, right) {
  return serializeReportQuery(left) === serializeReportQuery(right);
}

export function validateReportFilters(filters) {
  if (!validDate(filters.dateFrom) || !validDate(filters.dateTo)) {
    return 'components.pages.reports.filters.invalidDate';
  }

  if (moment(filters.dateFrom).isAfter(moment(filters.dateTo))) {
    return 'components.pages.reports.filters.invalidRange';
  }

  return null;
}

export { VALID_ORDER_TYPES };
